import type { PackLayout } from '@/packs'
import type { CoreNavKey } from '@/kinds/workspace/schema'
import {
  CalendarDays,
  FileText,
  FolderKanban,
  History,
  Inbox,
  ListFilter,
  RotateCcw,
  Search,
  Sparkles,
  Sun,
  type LucideIcon,
  type LucideProps,
} from 'lucide-react'
import type { ComponentType } from 'react'
import { isLineIcon, lineIcon } from './icons'

export type NavSection = 'plan' | 'organise' | 'pages' | 'projects' | 'dev' | 'infra' | 'connected' | 'more'

/** Sidebar groups, in order. Each is collapsible; the state is per device. */
export const NAV_SECTIONS: { key: NavSection; label: string }[] = [
  { key: 'plan', label: 'Plan' },
  { key: 'organise', label: 'Organise' },
  { key: 'pages', label: 'Pages' },
  { key: 'projects', label: 'Projects' },
  { key: 'dev', label: 'Dev' },
  // the infra pack's pages (2026-10-31): Overview, Hosts and Storage
  { key: 'infra', label: 'Infrastructure' },
  { key: 'connected', label: 'Connected' },
  { key: 'more', label: 'More' },
]

export interface NavItem {
  key: string
  to: string
  label: string
  /** glyph fallback (command palette, packs, pinned pages carry emoji here) */
  icon: string
  /** line icon for the sidebar and tab bar (a lucide icon, or one that takes its props, like PrimeMark) */
  Icon?: ComponentType<LucideProps>
  section: NavSection
  /** active only on an exact path match (a hub whose children are separate items) */
  exact?: boolean
  /** label for the phone tab bar, when the sidebar one is too long */
  short?: string
}

/**
 * Merged apps (2026-09-24): one app, several pages behind a switch at its
 * top. The app is active on any of them.
 */
export const ALSO_ACTIVE: Record<string, string[]> = {
  '/upcoming': ['/calendar', '/day', '/recurring'],
  '/review': ['/someday', '/plan'],
  '/pages': ['/lists'],
  '/music': ['/library'],
  '/log': ['/trash', '/activity', '/captures'],
}

/** The three apps that share /agents: Prime (its thread), the Graph, and Agents (every other agent). */
export type AgentsApp = '/agents' | '/agents/all' | '/agents/graph'

/**
 * Which of the three an /agents path belongs to, or null for any other path.
 * Prime is its own thread (`/agents/run/<lead>`), `/agents` and the chats under
 * it (the quick chat, a new chat); Agents is the list, the stats, the pipeline and every
 * other agent's transcript; the Graph is the Graph. Without the lead's id a
 * transcript counts as Agents. So exactly one of them is lit on any of these
 * pages, in the sidebar, the tab bar (the Prime app's too) and the launcher.
 */
export const agentsApp = (path: string, lead?: string): AgentsApp | null => {
  if (path !== '/agents' && !path.startsWith('/agents/')) return null
  if (path.startsWith('/agents/graph')) return '/agents/graph'
  if (
    path.startsWith('/agents/all') ||
    path.startsWith('/agents/stats') ||
    path.startsWith('/agents/pipeline')
  )
    return '/agents/all'
  if (path.startsWith('/agents/run/'))
    return lead && path === `/agents/run/${lead}` ? '/agents' : '/agents/all'
  return '/agents'
}

const AGENTS_APPS = new Set<string>(['/agents', '/agents/all', '/agents/graph'])

/**
 * Whether the nav entry at `to` is the page at `path`, counting its merged
 * pages. `lead` is Prime's run id, so its thread lights Prime, not Agents.
 */
export const navActive = (to: string, path: string, exact = false, lead?: string): boolean => {
  const agents = agentsApp(path, lead)
  if (agents && AGENTS_APPS.has(to)) return to === agents
  return (
    (exact ? path === to : to === '/' ? path === '/' : path.startsWith(to)) ||
    (ALSO_ACTIVE[to] ?? []).some((p) => path.startsWith(p))
  )
}

/**
 * Old app ids that now live inside another app: a dock slot or Home icon kept
 * from before the merge opens the app it joined.
 */
export const MERGED_INTO: Record<string, string> = {
  calendar: 'upcoming',
  captures: 'log',
  someday: 'review',
  recurring: 'upcoming',
  lists: 'pages',
  library: 'music',
  trash: 'log',
  activity: 'log',
  // the desk's unique cards became Home widgets (2026-10-10)
  desk: 'home',
  // DevOS's list pages, now views of the Graph (2026-10-10, "Graph is the hub")
  '/dev/board': 'graph',
  '/dev/reviews': 'graph',
  '/dev/issues': 'graph',
  '/dev/overview': 'graph',
  '/dev/infra': 'graph',
  '/dev/runs': 'agents',
}

/** Core views, keyed so a workspace can pick which ones it shows (spec v3 §1.2). */
/** Pinned pages, lists and projects are keyed `page-<id>` / `list-<id>` / `project-<id>`. */
export const isPinnedNav = (key: string) =>
  key.startsWith('page-') || key.startsWith('list-') || key.startsWith('project-')

/** The projects that show as apps: pinned, live, still open (not done, dropped or converted), by title. */
export const pinnedProjects = <
  P extends {
    spec: { title: string; pinned?: boolean }
    status: { state: string; converted_to?: unknown }
    deleted_at?: string | null
  },
>(
  projects: P[] | undefined,
  max = 12,
): P[] =>
  (projects ?? [])
    .filter(
      (p) =>
        p.spec.pinned &&
        !p.deleted_at &&
        !p.status.converted_to &&
        (p.status.state === 'active' || p.status.state === 'paused'),
    )
    .sort((a, b) => a.spec.title.localeCompare(b.spec.title))
    .slice(0, max)

export const CORE_NAV: (NavItem & { key: CoreNavKey })[] = [
  { key: 'today', to: '/', label: 'Today', icon: '◉', Icon: Sun, section: 'plan' },
  { key: 'inbox', to: '/inbox', label: 'Inbox', icon: '▤', Icon: Inbox, section: 'plan' },
  // the calendar: the days ahead as a list, a day by the hour, and a month
  { key: 'upcoming', to: '/upcoming', label: 'Calendar', icon: '▦', Icon: CalendarDays, section: 'plan' },
  {
    key: 'projects',
    to: '/projects',
    label: 'All projects',
    short: 'Projects',
    icon: '▣',
    Icon: FolderKanban,
    section: 'projects',
  },
  // pages and lists together
  {
    key: 'pages',
    to: '/pages',
    label: 'Notes',
    icon: '📄',
    Icon: FileText,
    section: 'pages',
    exact: true,
  },
  // the weekly review, with Someday as its step
  { key: 'review', to: '/review', label: 'Review', icon: '↻', Icon: RotateCcw, section: 'organise' },
  // every agent (Prime has its own app, which opens its thread)
  { key: 'agents', to: '/agents/all', label: 'Agents', icon: '✨', Icon: Sparkles, section: 'more' },
  // what was done, what was deleted, and the system's own trail
  { key: 'log', to: '/log', label: 'History', icon: '✓', Icon: History, section: 'more' },
  // every task, project, page and list, filtered by a small query language, with queries pinned as tabs
  { key: 'all', to: '/all', label: 'All', icon: '☰', Icon: ListFilter, section: 'organise' },
  { key: 'search', to: '/search', label: 'Search', icon: '⌕', Icon: Search, section: 'more' },
]

/**
 * Applies the enabled packs' layouts to the nav entries: adopted entries move
 * into the pack's section, its order is applied, and (when `hide`) the entries
 * it hides drop out. `hide` is off under an explicit `spec.nav`; Everything
 * applies no layout at all (laidOutNav).
 */
export const applyPackLayouts = (items: NavItem[], layouts: PackLayout[], hide: boolean): NavItem[] => {
  const hidden = new Set(hide ? layouts.flatMap((l) => l.hides ?? []) : [])
  let out = items.filter((n) => !hidden.has(n.key))
  for (const l of layouts) {
    out = out.map((n) => (l.adopts?.includes(n.key) ? { ...n, section: l.section } : n))
    const order = l.order ?? []
    const rank = (n: NavItem) => {
      const i = order.indexOf(n.key)
      return i < 0 ? order.length : i
    }
    // a stable sort of the section's own entries, left in the slots they held
    const slots = out.flatMap((n, i) => (n.section === l.section ? [i] : []))
    const sorted = slots.map((i) => out[i]!).sort((a, b) => rank(a) - rank(b))
    slots.forEach((i, j) => (out[i] = sorted[j]!))
  }
  return out
}

/** The sidebar's section order: a pack that asks to be first puts its section above the rest. */
export const sectionOrder = (layouts: PackLayout[], firstWanted: boolean) => {
  const first = firstWanted ? layouts.find((l) => l.first)?.section : undefined
  return first
    ? [...NAV_SECTIONS.filter((s) => s.key === first), ...NAV_SECTIONS.filter((s) => s.key !== first)]
    : NAV_SECTIONS
}

/**
 * The sidebar's entries and section order. In a workspace the packs' layouts
 * apply (hides only with no hand-set `nav`). In Everything they do not: a
 * layout is one workspace's choice, so Everything keeps the plain sections,
 * pack pages under Connected and Prime and Agents under More.
 */
export const laidOutNav = (
  core: NavItem[],
  pack: NavItem[],
  extras: NavItem[],
  layouts: PackLayout[],
  o: { workspace: boolean; handSet: boolean },
): { items: NavItem[]; order: typeof NAV_SECTIONS } =>
  o.workspace
    ? {
        items: applyPackLayouts([...core, ...pack, ...extras], layouts, !o.handSet),
        order: sectionOrder(layouts, true),
      }
    : {
        items: [...core, ...pack.map((n) => ({ ...n, section: 'connected' as const })), ...extras],
        order: NAV_SECTIONS,
      }

/** A resource's icon reference as nav fields: a line icon is drawn like the core ones, an emoji is text. */
export const navIconOf = (value: string | undefined, fallback: LucideIcon, glyph: string) => {
  const line = lineIcon(value)
  return line
    ? { icon: glyph, Icon: line }
    : value && !isLineIcon(value)
      ? { icon: value, Icon: undefined }
      : { icon: glyph, Icon: fallback }
}

/**
 * The workspace editor's Pages ticks. With no hand-set `nav`, what the
 * workspace's packs hide shows unticked, as the sidebar has it; ticking one
 * back writes an explicit list (which wins over the pack). A choice equal to
 * the default goes back to no list, so the pack's layout follows later changes.
 */
export const navTicks = (keys: string[], nav: string[] | null | undefined, hidden: string[]) => {
  const hide = new Set(nav == null ? hidden : [])
  return new Set(nav == null ? keys.filter((k) => !hide.has(k)) : nav)
}

export const navAfterTick = (
  keys: string[],
  nav: string[] | null | undefined,
  hidden: string[],
  key: string,
  on: boolean,
): string[] | undefined => {
  const next = navTicks(keys, nav, hidden)
  if (on) next.add(key)
  else next.delete(key)
  const fallback = navTicks(keys, undefined, hidden)
  const same = next.size === fallback.size && [...next].every((k) => fallback.has(k))
  return same ? undefined : keys.filter((k) => next.has(k))
}
